/**
 * Mobile Accessibility Manager
 * Provides comprehensive accessibility features for mobile devices
 */

/**
 * Focus Manager for Mobile Devices
 * Handles focus management and keyboard navigation
 */
export class MobileFocusManager {
  constructor() {
    this.focusableElements = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');
    
    this.trapStack = [];
    this.lastFocusedElement = null;
    
    this.init();
  }

  /**
   * Initialize focus management
   */
  init() {
    this.setupKeyboardNavigation();
    this.setupFocusTrapping();
    this.setupSkipLinks();
    this.enhanceFocusVisibility();
  }

  /**
   * Setup keyboard navigation for mobile
   */
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      // Enhanced Tab navigation
      if (e.key === 'Tab') {
        this.handleTabNavigation(e);
      }
      
      // Escape key for modal/overlay closing
      if (e.key === 'Escape') {
        this.handleEscapeKey(e);
      }
      
      // Arrow key navigation for lists and grids
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        this.handleArrowNavigation(e);
      }
    });
  }

  /**
   * Handle Tab navigation
   */
  handleTabNavigation(e) {
    const activeModal = document.querySelector('.modal.show');
    if (activeModal) {
      this.constrainFocusToModal(e, activeModal);
    }
  }

  /**
   * Handle Escape key press
   */
  handleEscapeKey(e) {
    const activeModal = document.querySelector('.modal.show');
    if (activeModal) {
      const closeBtn = activeModal.querySelector('.close');
      if (closeBtn) {
        closeBtn.click();
      }
    }
    
    // Close mobile navigation
    const openNav = document.querySelector('.nav-toggle.active');
    if (openNav) {
      openNav.click();
    }
  }

  /**
   * Handle arrow key navigation
   */
  handleArrowNavigation(e) {
    const target = e.target;
    
    // Navigate through button groups
    if (target.closest('.btn-group, .nav-pills, .pagination')) {
      e.preventDefault();
      this.navigateButtonGroup(e, target);
    }
    
    // Navigate through table cells
    if (target.closest('table')) {
      e.preventDefault();
      this.navigateTable(e, target);
    }
  }

  /**
   * Navigate through button groups
   */
  navigateButtonGroup(e, currentElement) {
    const container = currentElement.closest('.btn-group, .nav-pills, .pagination');
    const buttons = container.querySelectorAll('button:not([disabled]), a[href]');
    const currentIndex = Array.from(buttons).indexOf(currentElement);
    
    let nextIndex;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
    } else {
      nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
    }
    
    buttons[nextIndex].focus();
  }

  /**
   * Navigate through table cells
   */
  navigateTable(e, currentElement) {
    const table = currentElement.closest('table');
    const cell = currentElement.closest('td, th');
    const row = cell.parentElement;
    const cellIndex = Array.from(row.children).indexOf(cell);
    const rowIndex = Array.from(table.rows).indexOf(row);
    
    let targetCell;
    
    switch (e.key) {
      case 'ArrowUp':
        if (rowIndex > 0) {
          targetCell = table.rows[rowIndex - 1].children[cellIndex];
        }
        break;
      case 'ArrowDown':
        if (rowIndex < table.rows.length - 1) {
          targetCell = table.rows[rowIndex + 1].children[cellIndex];
        }
        break;
      case 'ArrowLeft':
        if (cellIndex > 0) {
          targetCell = row.children[cellIndex - 1];
        }
        break;
      case 'ArrowRight':
        if (cellIndex < row.children.length - 1) {
          targetCell = row.children[cellIndex + 1];
        }
        break;
    }
    
    if (targetCell) {
      const focusableInCell = targetCell.querySelector(this.focusableElements);
      if (focusableInCell) {
        focusableInCell.focus();
      } else {
        targetCell.focus();
      }
    }
  }

  /**
   * Setup focus trapping for modals
   */
  setupFocusTrapping() {
    // Modal focus trapping
    document.addEventListener('focusin', (e) => {
      const activeModal = document.querySelector('.modal.show');
      if (activeModal && !activeModal.contains(e.target)) {
        const firstFocusable = activeModal.querySelector(this.focusableElements);
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    });
  }

  /**
   * Constrain focus to modal
   */
  constrainFocusToModal(e, modal) {
    const focusableElements = modal.querySelectorAll(this.focusableElements);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Setup skip links
   */
  setupSkipLinks() {
    const skipLink = document.querySelector('.skip-link');
    if (skipLink) {
      skipLink.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(skipLink.getAttribute('href'));
        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  /**
   * Enhance focus visibility for mobile
   */
  enhanceFocusVisibility() {
    // Add focus-visible polyfill behavior
    document.addEventListener('keydown', () => {
      document.body.classList.add('keyboard-navigation');
    });
    
    document.addEventListener('mousedown', () => {
      document.body.classList.remove('keyboard-navigation');
    });
    
    document.addEventListener('touchstart', () => {
      document.body.classList.remove('keyboard-navigation');
    });
  }

  /**
   * Set focus trap
   */
  setFocusTrap(element) {
    this.lastFocusedElement = document.activeElement;
    this.trapStack.push(element);
    
    const firstFocusable = element.querySelector(this.focusableElements);
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  /**
   * Remove focus trap
   */
  removeFocusTrap() {
    this.trapStack.pop();
    
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }
}

/**
 * Screen Reader Utilities
 * Provides screen reader optimizations and announcements
 */
export class ScreenReaderManager {
  constructor() {
    this.liveRegion = null;
    this.politeRegion = null;
    this.assertiveRegion = null;
    
    this.init();
  }

  /**
   * Initialize screen reader utilities
   */
  init() {
    this.createLiveRegions();
    this.setupFormAnnouncements();
    this.setupNavigationAnnouncements();
    this.enhanceInteractiveElements();
  }

  /**
   * Create ARIA live regions
   */
  createLiveRegions() {
    // Polite announcements
    this.politeRegion = this.createLiveRegion('polite', 'sr-live-polite');
    
    // Assertive announcements
    this.assertiveRegion = this.createLiveRegion('assertive', 'sr-live-assertive');
    
    // General live region
    this.liveRegion = this.politeRegion;
  }

  /**
   * Create a live region element
   */
  createLiveRegion(level, id) {
    let region = document.getElementById(id);
    if (!region) {
      region = document.createElement('div');
      region.id = id;
      region.setAttribute('aria-live', level);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'live-region sr-only';
      document.body.appendChild(region);
    }
    return region;
  }

  /**
   * Announce message to screen readers
   */
  announce(message, priority = 'polite') {
    const region = priority === 'assertive' ? this.assertiveRegion : this.politeRegion;
    
    // Clear previous message
    region.textContent = '';
    
    // Set new message after a brief delay
    setTimeout(() => {
      region.textContent = message;
    }, 100);
    
    // Clear message after announcement
    setTimeout(() => {
      region.textContent = '';
    }, 1000);
  }

  /**
   * Setup form validation announcements
   */
  setupFormAnnouncements() {
    document.addEventListener('invalid', (e) => {
      const field = e.target;
      const label = this.getFieldLabel(field);
      const message = field.validationMessage;
      
      this.announce(`${label}: ${message}`, 'assertive');
    });
    
    // Success announcements
    document.addEventListener('input', (e) => {
      const field = e.target;
      if (field.checkValidity() && field.dataset.wasInvalid === 'true') {
        const label = this.getFieldLabel(field);
        this.announce(`${label} is now valid`);
        delete field.dataset.wasInvalid;
      } else if (!field.checkValidity()) {
        field.dataset.wasInvalid = 'true';
      }
    });
  }

  /**
   * Get field label for announcements
   */
  getFieldLabel(field) {
    const label = document.querySelector(`label[for="${field.id}"]`);
    if (label) {
      return label.textContent.trim();
    }
    
    const ariaLabel = field.getAttribute('aria-label');
    if (ariaLabel) {
      return ariaLabel;
    }
    
    const placeholder = field.getAttribute('placeholder');
    if (placeholder) {
      return placeholder;
    }
    
    return field.name || 'Field';
  }

  /**
   * Setup navigation announcements
   */
  setupNavigationAnnouncements() {
    // Page navigation announcements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.announceNewContent(node);
            }
          });
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Announce new content
   */
  announceNewContent(element) {
    // Announce new modal
    if (element.classList?.contains('modal') && element.classList?.contains('show')) {
      const title = element.querySelector('h1, h2, h3, .modal-title');
      const titleText = title ? title.textContent : 'Dialog opened';
      this.announce(`${titleText}. Press Escape to close.`, 'assertive');
    }
    
    // Announce toast notifications
    if (element.classList?.contains('toast')) {
      const message = element.textContent.trim();
      this.announce(message, 'assertive');
    }
    
    // Announce loading states
    if (element.classList?.contains('loading')) {
      this.announce('Loading content', 'polite');
    }
  }

  /**
   * Enhance interactive elements with better descriptions
   */
  enhanceInteractiveElements() {
    // Enhance buttons without visible text
    document.querySelectorAll('button:not([aria-label])').forEach(button => {
      if (!button.textContent.trim()) {
        const icon = button.querySelector('i, svg, .icon');
        if (icon) {
          const classes = Array.from(icon.classList);
          const iconName = classes.find(cls => cls.includes('icon-') || cls.includes('fa-'));
          if (iconName) {
            button.setAttribute('aria-label', this.getButtonLabelFromIcon(iconName));
          }
        }
      }
    });
    
    // Enhance links that open in new windows
    document.querySelectorAll('a[target="_blank"]:not([aria-label])').forEach(link => {
      const currentLabel = link.getAttribute('aria-label') || link.textContent.trim();
      link.setAttribute('aria-label', `${currentLabel} (opens in new window)`);
    });
  }

  /**
   * Get button label from icon class
   */
  getButtonLabelFromIcon(iconClass) {
    const iconMappings = {
      'icon-edit': 'Edit',
      'icon-delete': 'Delete',
      'icon-close': 'Close',
      'icon-menu': 'Menu',
      'icon-search': 'Search',
      'icon-filter': 'Filter',
      'icon-sort': 'Sort',
      'fa-edit': 'Edit',
      'fa-trash': 'Delete',
      'fa-times': 'Close',
      'fa-bars': 'Menu',
      'fa-search': 'Search'
    };
    
    return iconMappings[iconClass] || 'Button';
  }
}

/**
 * Voice Navigation Manager
 * Provides voice command support for mobile accessibility
 */
export class VoiceNavigationManager {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.commands = new Map();
    
    this.init();
  }

  /**
   * Initialize voice navigation
   */
  init() {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      this.setupSpeechRecognition();
      this.setupVoiceCommands();
    }
  }

  /**
   * Setup speech recognition
   */
  setupSpeechRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    
    this.recognition.continuous = false;
    this.recognition.interimResults = false;
    this.recognition.lang = 'en-US';
    
    this.recognition.onresult = (event) => {
      const command = event.results[0][0].transcript.toLowerCase().trim();
      this.processCommand(command);
    };
    
    this.recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      this.isListening = false;
    };
    
    this.recognition.onend = () => {
      this.isListening = false;
    };
  }

  /**
   * Setup voice commands
   */
  setupVoiceCommands() {
    // Register common commands
    this.registerCommand('click', (element) => {
      if (element && element.click) {
        element.click();
        return true;
      }
      return false;
    });
    
    this.registerCommand('focus', (element) => {
      if (element && element.focus) {
        element.focus();
        return true;
      }
      return false;
    });
    
    this.registerCommand('scroll up', () => {
      window.scrollBy(0, -300);
      return true;
    });
    
    this.registerCommand('scroll down', () => {
      window.scrollBy(0, 300);
      return true;
    });
    
    this.registerCommand('go back', () => {
      window.history.back();
      return true;
    });
    
    this.registerCommand('close', () => {
      const modal = document.querySelector('.modal.show .close');
      if (modal) {
        modal.click();
        return true;
      }
      return false;
    });
  }

  /**
   * Register a voice command
   */
  registerCommand(phrase, handler) {
    this.commands.set(phrase.toLowerCase(), handler);
  }

  /**
   * Process voice command
   */
  processCommand(command) {
    // Try exact match first
    if (this.commands.has(command)) {
      const handler = this.commands.get(command);
      if (handler()) {
        this.announceSuccess(`Executed: ${command}`);
        return;
      }
    }
    
    // Try partial matches for element-specific commands
    const words = command.split(' ');
    const action = words[0];
    const target = words.slice(1).join(' ');
    
    if (this.commands.has(action)) {
      const element = this.findElementByText(target);
      if (element) {
        const handler = this.commands.get(action);
        if (handler(element)) {
          this.announceSuccess(`${action} ${target}`);
          return;
        }
      }
    }
    
    this.announceError(`Command not recognized: ${command}`);
  }

  /**
   * Find element by text content
   */
  findElementByText(text) {
    const elements = document.querySelectorAll('button, a, [role="button"], [data-voice-command]');
    
    for (const element of elements) {
      const elementText = (element.textContent || element.getAttribute('aria-label') || '').toLowerCase();
      const voiceCommand = element.getAttribute('data-voice-command');
      
      if (elementText.includes(text) || (voiceCommand && voiceCommand.toLowerCase().includes(text))) {
        return element;
      }
    }
    
    return null;
  }

  /**
   * Start listening for voice commands
   */
  startListening() {
    if (this.recognition && !this.isListening) {
      this.isListening = true;
      this.recognition.start();
    }
  }

  /**
   * Stop listening for voice commands
   */
  stopListening() {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }

  /**
   * Announce success message
   */
  announceSuccess(message) {
    const event = new CustomEvent('voice-command-success', { detail: { message } });
    document.dispatchEvent(event);
  }

  /**
   * Announce error message
   */
  announceError(message) {
    const event = new CustomEvent('voice-command-error', { detail: { message } });
    document.dispatchEvent(event);
  }
}

/**
 * Mobile Accessibility Manager
 * Main coordinator for all accessibility features
 */
export class MobileAccessibilityManager {
  constructor() {
    this.focusManager = null;
    this.screenReaderManager = null;
    this.voiceNavigationManager = null;
    this.highContrastMode = false;
    this.largeTextMode = false;
    this.reducedMotion = false;
    
    this.init();
  }

  /**
   * Initialize all accessibility managers
   */
  init() {
    this.focusManager = new MobileFocusManager();
    this.screenReaderManager = new ScreenReaderManager();
    this.voiceNavigationManager = new VoiceNavigationManager();
    
    this.setupPreferences();
    this.setupGlobalEnhancements();
    this.setupEventListeners();
  }

  /**
   * Setup user preferences
   */
  setupPreferences() {
    // Detect system preferences
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // Apply stored preferences
    const preferences = this.loadPreferences();
    if (preferences.highContrast) {
      this.toggleHighContrast(true);
    }
    if (preferences.largeText) {
      this.toggleLargeText(true);
    }
  }

  /**
   * Setup global accessibility enhancements
   */
  setupGlobalEnhancements() {
    // Add skip links if not present
    if (!document.querySelector('.skip-link')) {
      this.addSkipLinks();
    }
    
    // Enhance form validation
    this.enhanceFormValidation();
    
    // Setup announcement listeners
    this.setupAnnouncementListeners();
  }

  /**
   * Add skip links to page
   */
  addSkipLinks() {
    const skipLink = document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.className = 'skip-link';
    skipLink.textContent = 'Skip to main content';
    
    document.body.insertBefore(skipLink, document.body.firstChild);
    
    // Ensure main content has proper ID
    const main = document.querySelector('main, [role="main"], .main-content');
    if (main && !main.id) {
      main.id = 'main-content';
      main.tabIndex = -1;
    }
  }

  /**
   * Enhance form validation
   */
  enhanceFormValidation() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const invalidFields = form.querySelectorAll(':invalid');
      
      if (invalidFields.length > 0) {
        e.preventDefault();
        const firstInvalid = invalidFields[0];
        firstInvalid.focus();
        
        const label = this.screenReaderManager.getFieldLabel(firstInvalid);
        this.screenReaderManager.announce(
          `Form submission failed. ${label} has an error: ${firstInvalid.validationMessage}`,
          'assertive'
        );
      }
    });
  }

  /**
   * Setup announcement listeners
   */
  setupAnnouncementListeners() {
    // Voice command announcements
    document.addEventListener('voice-command-success', (e) => {
      this.screenReaderManager.announce(e.detail.message, 'polite');
    });
    
    document.addEventListener('voice-command-error', (e) => {
      this.screenReaderManager.announce(e.detail.message, 'assertive');
    });
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for preference changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      this.reducedMotion = e.matches;
      this.applyReducedMotion();
    });
    
    window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
      if (e.matches && !this.highContrastMode) {
        this.toggleHighContrast(true);
      }
    });
  }

  /**
   * Toggle high contrast mode
   */
  toggleHighContrast(enable = !this.highContrastMode) {
    this.highContrastMode = enable;
    document.body.classList.toggle('high-contrast-mode', enable);
    
    this.savePreferences();
    this.screenReaderManager.announce(
      `High contrast mode ${enable ? 'enabled' : 'disabled'}`,
      'polite'
    );
  }

  /**
   * Toggle large text mode
   */
  toggleLargeText(enable = !this.largeTextMode) {
    this.largeTextMode = enable;
    document.body.classList.toggle('large-text-mode', enable);
    
    this.savePreferences();
    this.screenReaderManager.announce(
      `Large text mode ${enable ? 'enabled' : 'disabled'}`,
      'polite'
    );
  }

  /**
   * Apply reduced motion preferences
   */
  applyReducedMotion() {
    document.body.classList.toggle('respect-motion-preference', this.reducedMotion);
  }

  /**
   * Save user preferences
   */
  savePreferences() {
    const preferences = {
      highContrast: this.highContrastMode,
      largeText: this.largeTextMode,
      reducedMotion: this.reducedMotion
    };
    
    localStorage.setItem('accessibility-preferences', JSON.stringify(preferences));
  }

  /**
   * Load user preferences
   */
  loadPreferences() {
    try {
      const stored = localStorage.getItem('accessibility-preferences');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Could not load accessibility preferences:', error);
      return {};
    }
  }

  /**
   * Announce message to screen readers
   */
  announce(message, priority = 'polite') {
    this.screenReaderManager.announce(message, priority);
  }

  /**
   * Start voice navigation
   */
  startVoiceNavigation() {
    this.voiceNavigationManager.startListening();
  }

  /**
   * Stop voice navigation
   */
  stopVoiceNavigation() {
    this.voiceNavigationManager.stopListening();
  }

  /**
   * Static initialization method
   */
  static init() {
    if (typeof window !== 'undefined') {
      return new MobileAccessibilityManager();
    }
  }
}

// Auto-initialize
export default MobileAccessibilityManager;
